import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { auth } from "./api/client";
import "./index.css";

// Pages
import PublicCheck from "./pages/PublicCheck";
import Kiosk from "./pages/Kiosk";
import AdminLogin from "./pages/AdminLogin";
import Dashboard from "./pages/Dashboard";
import Upload from "./pages/Upload";
import Recipients from "./pages/Recipients";
import AttendanceLog from "./pages/AttendanceLog";
import Institutions from "./pages/Institutions";

// Loading component
function LoadingScreen() {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-600">Loading...</p>
      </div>
    </div>
  );
}

// Protected Route Wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const session = await auth.getSession();
      setIsAuthenticated(!!session.data?.user);
      setIsLoading(false);
    };
    checkAuth();
  }, []);

  if (isLoading) return <LoadingScreen />;
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return <>{children}</>;
}

// Admin Layout
function AdminLayout({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    const checkAuth = async () => {
      const session = await auth.getSession();
      if (session.data?.user) {
        setUser(session.data.user);
      }
      setIsLoading(false);
    };
    checkAuth();
  }, []);

  const handleLogout = async () => {
    await auth.signOut();
    window.location.href = "/admin/login";
  };

  if (isLoading) return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-6">
              <a href="/admin/dashboard" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">B</span>
                </div>
                <span className="font-semibold text-slate-800">Bantuan Distribution</span>
              </a>
              <nav className="hidden md:flex gap-1">
                <NavLink to="/admin/dashboard">Dashboard</NavLink>
                <NavLink to="/admin/institutions">Institusi</NavLink>
                <NavLink to="/admin/upload">Muat Naik</NavLink>
                <NavLink to="/admin/recipients">Penerima</NavLink>
                <NavLink to="/admin/attendance">Log Kehadiran</NavLink>
              </nav>
            </div>
            <div className="flex items-center gap-4">
              {user && (
                <span className="text-sm text-slate-600 hidden sm:block">
                  {user.name}
                </span>
              )}
              <button
                onClick={handleLogout}
                className="text-sm text-slate-600 hover:text-slate-800 px-3 py-2 rounded-lg hover:bg-slate-100 transition"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      <nav className="md:hidden bg-white border-b border-slate-200 px-4 py-2 flex gap-2 overflow-x-auto">
        <NavLinkMobile to="/admin/dashboard">Dashboard</NavLinkMobile>
        <NavLinkMobile to="/admin/institutions">Institusi</NavLinkMobile>
        <NavLinkMobile to="/admin/upload">Muat Naik</NavLinkMobile>
        <NavLinkMobile to="/admin/recipients">Penerima</NavLinkMobile>
        <NavLinkMobile to="/admin/attendance">Log</NavLinkMobile>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {children}
      </main>
    </div>
  );
}

function NavLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <a
      href={to}
      className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition"
    >
      {children}
    </a>
  );
}

function NavLinkMobile({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <a
      href={to}
      className="px-3 py-2 text-sm font-medium text-slate-600 whitespace-nowrap"
    >
      {children}
    </a>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Navigate to="/check" replace />} />
        <Route path="/check" element={<PublicCheck />} />
        <Route path="/kiosk" element={<Kiosk />} />
        
        {/* Admin Routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <Dashboard />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/institutions"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <Institutions />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/upload"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <Upload />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/recipients"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <Recipients />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/attendance"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <AttendanceLog />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
